import { QueryHookOptions, useApolloClient } from "@apollo/client";
import { ForwardedRef, forwardRef, useCallback } from "react";
import { GroupBase, SelectInstance } from "react-select";
import AsyncSelect, { AsyncProps } from "react-select/async";
import useIdValue, {
  IdValueSelectProps,
} from "components/Shared/Select/useIdValue";
import useAsyncFetchOptions from "components/Shared/Select/useAsyncFetchOptions";
import { ADMIN_CONTEXT } from "utils/apollo/hooks/admin";
import { getActiveBrandIdContext } from "utils/apollo/hooks/brand";
import { assertBrandId, assertPresence } from "utils/assertions";
import { useCurrentBrandId } from "utils/currentUserHooks";
import useSelectStyles, { SelectThemingProps } from "utils/useSelectStyles";
import { gql, getFragmentData, DocumentType } from "__generated__/gql";
import { DocumentVariablesType } from "utils/apollo/hooks";
import { OrderingPlatformFilter } from "__generated__/gql/graphql";
import useLatestRef from "utils/useLatestRef";
import { isNewOption } from "components/Shared/Select/utils";

const OPTION_LIMIT = 15;

const optionFragment = gql(/* GraphQL */ `
  fragment OrderingPlatformSelectOption on OrderingPlatform {
    id
    name
  }
`);

export type Option = DocumentType<typeof optionFragment>;

const valueQuery = gql(/* GraphQL */ `
  query OrderingPlatformSelectValue($ids: [UUID!]!) {
    orderingPlatformsList(filter: { id: { in: $ids } }) {
      ...OrderingPlatformSelectOption
    }
  }
`);

const query = gql(/* GraphQL */ `
  query OrderingPlatformSelectOptions(
    $brandId: UUID!
    $filter: OrderingPlatformFilter
    $limit: Int = 5
  ) {
    orderingPlatformsForBrand(
      brandId: $brandId
      filter: $filter
      orderBy: NAME_ASC
      first: $limit
    ) {
      totalCount
      nodes {
        ...OrderingPlatformSelectOption
      }
    }
  }
`);

const adminQuery = gql(/* GraphQL */ `
  query AdminOrderingPlatformSelectOptions(
    $filter: OrderingPlatformFilter
    $limit: Int = 5
  ) {
    orderingPlatforms(
      condition: { forBrandId: null }
      filter: $filter
      orderBy: NAME_ASC
      first: $limit
    ) {
      totalCount
      nodes {
        ...OrderingPlatformSelectOption
      }
    }
  }
`);

function noOptionsMessage({ inputValue }: { inputValue: string }) {
  if (inputValue?.length > 1)
    return <>No customers found containing: “{inputValue}”</>;
  return "Enter at least two characters to search…";
}

function getOptionLabel(option: Option) {
  if (isNewOption(option)) return option.label;
  return option.name;
}

export type OrderingPlatformSelectProps<IsMulti extends boolean = false> = Omit<
  AsyncProps<Option, IsMulti, GroupBase<Option>>,
  "onChange" | "value"
> &
  IdValueSelectProps<IsMulti> & {
    valueQueryOptions?: Omit<
      QueryHookOptions<
        DocumentType<typeof valueQuery>,
        DocumentVariablesType<typeof valueQuery>
      >,
      "variables"
    >;
  } & {
    excludedIds?: string[];
    isAddedToBrand?: boolean;
    isAdmin?: boolean;
    filter?: OrderingPlatformFilter;
    themingProps?: SelectThemingProps;
  };

export function useOrderingPlatformSelect<IsMulti extends boolean = false>({
  components: componentsProp,
  excludedIds,
  isAddedToBrand,
  isAdmin,
  isMulti,
  filter: filterProp,
  onChange,
  styles,
  themingProps,
  value: idValue,
  valueQueryOptions,
  ...rest
}: OrderingPlatformSelectProps<IsMulti>) {
  const styleProps = useSelectStyles<Option, IsMulti>(styles, themingProps);
  const client = useApolloClient();
  const brandId = useCurrentBrandId();

  const idValueProps = useIdValue<Option, IsMulti, typeof valueQuery>({
    getOptionsFromData: (data) =>
      getFragmentData(optionFragment, data.orderingPlatformsList),
    getOptionLabel,
    isMulti,
    onChange,
    query: valueQuery,
    queryOptions: valueQueryOptions,
    value: idValue,
  });

  const latestFilterPropRef = useLatestRef(filterProp);
  const latestExcludedIdsRef = useLatestRef(excludedIds);

  const asyncFetchProps = useAsyncFetchOptions({
    cacheUsing: filterProp,
    fetchOptions: useCallback(
      (inputValue) => {
        const baseFilter: OrderingPlatformFilter = /^[0-9a-f-]{32,36}$/i.test(
          inputValue,
        )
          ? { id: { equalTo: inputValue } }
          : { name: { includesInsensitive: inputValue } };

        const filters: OrderingPlatformFilter[] = [baseFilter];

        if (latestExcludedIdsRef.current?.length) {
          filters.push({ id: { notIn: latestExcludedIdsRef.current } });
        }

        if (latestFilterPropRef.current) {
          filters.push(latestFilterPropRef.current);
        }

        if (isAddedToBrand) {
          filters.push({
            brandOrderingPlatforms: { some: { brandId: { equalTo: brandId } } },
          });
        }

        const filter: OrderingPlatformFilter =
          filters.length > 1 ? { and: filters } : filters[0];

        if (isAdmin) {
          return client
            .query({
              query: adminQuery,
              variables: { filter, limit: OPTION_LIMIT },
              fetchPolicy: "network-only",
              context: ADMIN_CONTEXT,
            })
            .then(({ data }) => {
              assertPresence(
                data.orderingPlatforms,
                "Could not get ordering platforms from response.",
              );
              const { nodes, totalCount } = data.orderingPlatforms;
              const options = getFragmentData(optionFragment, nodes);
              return { options, totalCount };
            });
        }

        assertBrandId(brandId);

        return client
          .query({
            query,
            variables: { brandId, filter, limit: OPTION_LIMIT },
            fetchPolicy: "network-only",
            context: getActiveBrandIdContext(brandId),
          })
          .then(({ data }) => {
            assertPresence(
              data.orderingPlatformsForBrand,
              "Could not get ordering platforms from response.",
            );
            const { nodes, totalCount } = data.orderingPlatformsForBrand;
            const options = getFragmentData(optionFragment, nodes);
            return { options, totalCount };
          });
      },
      [
        isAddedToBrand,
        isAdmin,
        brandId,
        client,
        latestFilterPropRef,
        latestExcludedIdsRef,
      ],
    ),
  });

  return {
    ...styleProps,
    ...idValueProps,
    ...asyncFetchProps,
    isMulti,
    noOptionsMessage,
    placeholder: "Select a customer…",
    components: {
      ...styleProps.components,
      ...asyncFetchProps.components,
      ...componentsProp,
    },
    ...rest,
  };
}

export type OrderingPlatformSelectInstance<IsMulti extends boolean = false> =
  SelectInstance<Option, IsMulti>;

function OrderingPlatformSelectInner<IsMulti extends boolean = false>(
  props: OrderingPlatformSelectProps<IsMulti>,
  ref: ForwardedRef<OrderingPlatformSelectInstance<IsMulti>>,
) {
  const selectProps = useOrderingPlatformSelect<IsMulti>(props);

  return <AsyncSelect<Option, IsMulti> ref={ref} {...selectProps} />;
}

export default forwardRef(OrderingPlatformSelectInner);
